import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { RECORDING_POLICY_EASY_READ_COPY } from '@shared/calls/recording-consent'

function RecordingIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
      <circle cx="12" cy="12" r="7" />
      <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
    </svg>
  )
}

function LineIcon() {
  return (
    <span aria-hidden className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
      <svg viewBox="0 0 20 20" className="h-3 w-3" fill="currentColor">
        <circle cx="10" cy="10" r="4" />
      </svg>
    </span>
  )
}

export type RecordingConsentContentProps = {
  /** Members / Independents answer Yes / No; providers read the notice. */
  variant: 'consent' | 'acknowledge'
  /** An admin used "Resend consent". */
  resend?: boolean
  /** Context line under the heading (e.g. "Before your call to Sam"). */
  lead?: ReactNode
  /** A status line above the question (e.g. the current answer in Settings). */
  status?: ReactNode
  busy: 'accept' | 'decline' | null
  error: string | null
  onAccept: () => void
  /**
   * Members only: "No" asks "Are you sure?" first, then calls this. Resolve
   * `false` when saving failed (the confirm screen stays, with the error).
   */
  onDecline?: () => void | boolean | Promise<void | boolean>
  /** "Decide later" / "Not now" / "Go back". Absent hides it. */
  onLater?: () => void
  laterLabel?: string
  /** Hide the Yes button (the answer is already yes). */
  hideAccept?: boolean
  /** Hide the No button (the answer is already no). */
  hideDecline?: boolean
  acceptLabel?: string
  declineLabel?: string
  /** Focus the heading when this mounts (modals): never a button, so a held Enter can't answer. */
  autoFocusHeading?: boolean
  headingLevel?: 'h1' | 'h2'
  /** Use this id for the heading (the enclosing dialog is labelled by it). */
  headingId?: string
  /** The answer was saved: show this instead of the buttons. */
  result?: string | null
}

/**
 * The call-recording question in Easy Read, the same everywhere it is asked:
 * setup, the pre-dial gate and Menu → Call recording. Nothing here ever
 * answers by itself.
 */
export function RecordingConsentContent({
  variant,
  resend = false,
  lead,
  status,
  busy,
  error,
  onAccept,
  onDecline,
  onLater,
  laterLabel,
  hideAccept = false,
  hideDecline = false,
  acceptLabel,
  declineLabel,
  autoFocusHeading = true,
  headingLevel = 'h2',
  headingId: headingIdProp,
  result = null,
}: RecordingConsentContentProps) {
  const copy = RECORDING_POLICY_EASY_READ_COPY
  const ownHeadingId = useId()
  const headingId = headingIdProp ?? ownHeadingId
  const linesId = useId()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [confirming, setConfirming] = useState(false)
  const Heading = headingLevel

  useEffect(() => {
    if (autoFocusHeading) headingRef.current?.focus()
  }, [autoFocusHeading, confirming])

  const confirmDecline = async () => {
    if (!onDecline) return
    const ok = await onDecline()
    if (ok !== false) setConfirming(false)
  }

  if (confirming && onDecline && !result) {
    const confirm = copy.consent.declineConfirm
    return (
      <div aria-labelledby={headingId} role="group">
        <Heading
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          className="text-xl font-bold tracking-tight text-slate-900 outline-none"
        >
          {confirm.title}
        </Heading>
        <ul className="mt-3 space-y-2">
          {confirm.lines.map((line) => (
            <li key={line} className="text-[17px] leading-relaxed text-slate-700">
              {line}
            </li>
          ))}
        </ul>
        {error && (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}
        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => void confirmDecline()}
            disabled={busy !== null}
            className="min-h-12 w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-3 text-base font-semibold text-slate-900 outline-none transition hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-60"
          >
            {busy === 'decline' ? 'Saving…' : confirm.confirmLabel}
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            disabled={busy !== null}
            className="min-h-12 w-full rounded-xl px-4 py-3 text-base font-medium text-slate-600 outline-none transition hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            {confirm.cancelLabel}
          </button>
        </div>
      </div>
    )
  }

  const body = variant === 'consent' ? copy.consent : copy.acknowledge
  return (
    <div aria-labelledby={headingId} aria-describedby={linesId} role="group">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
        <RecordingIcon />
      </div>
      <Heading
        id={headingId}
        ref={headingRef}
        tabIndex={-1}
        className="mt-3 text-xl font-bold tracking-tight text-slate-900 outline-none"
      >
        {body.title}
      </Heading>
      {lead && <p className="mt-1 text-[15px] text-slate-600">{lead}</p>}
      {resend && (
        <p className="mt-2 rounded-lg bg-sky-50 px-3 py-2 text-[15px] font-medium text-sky-900">{body.resendLine}</p>
      )}
      <ul id={linesId} className="mt-3 space-y-2">
        {body.lines.map((line) => (
          <li key={line} className="flex items-start gap-2.5 text-[17px] leading-relaxed text-slate-700">
            <LineIcon />
            <span>{line}</span>
          </li>
        ))}
      </ul>
      {status && <div className="mt-3">{status}</div>}
      {variant === 'consent' && !hideAccept && (
        <p className="mt-4 text-[17px] font-semibold text-slate-900">{copy.consent.question}</p>
      )}
      {error && (
        <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
      {result ? (
        <p className="mt-4 rounded-xl bg-emerald-50 px-3.5 py-3 text-[17px] font-medium text-emerald-900" role="status">
          {result}
        </p>
      ) : (
      <div className="mt-4 flex flex-col gap-2">
        {!hideAccept && (
          <button
            type="button"
            onClick={onAccept}
            disabled={busy !== null}
            className="min-h-12 w-full rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 px-4 py-3 text-base font-semibold text-white shadow-md shadow-brand-600/25 outline-none transition hover:from-brand-600 hover:to-brand-700 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy === 'accept' ? 'Saving…' : (acceptLabel ?? body.acceptLabel)}
          </button>
        )}
        {variant === 'consent' && onDecline && !hideDecline && (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={busy !== null}
            className="min-h-12 w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-3 text-base font-semibold text-slate-900 outline-none transition hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-60"
          >
            {declineLabel ?? copy.consent.declineLabel}
          </button>
        )}
        {onLater && (
          <button
            type="button"
            onClick={onLater}
            disabled={busy !== null}
            className="min-h-11 w-full rounded-xl px-4 py-2.5 text-sm font-medium text-slate-500 underline-offset-2 outline-none transition hover:text-slate-700 hover:underline focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            {laterLabel ?? (variant === 'consent' ? copy.consent.laterLabel : copy.acknowledge.laterLabel)}
          </button>
        )}
      </div>
      )}
    </div>
  )
}
